import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Loader2, Phone, Mail, MapPin, Calendar, Clock, ImageIcon, Star, Check, X, Inbox,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectsPanel from "@/pages/admin/ProjectsPanel";
import SiteMediaPanel from "@/pages/admin/SiteMediaPanel";

const STATUSES = ["New", "Contacted", "Quoted", "Scheduled", "Completed", "Closed"];
const STATUS_COLORS = {
  New: "bg-amber-400/15 text-amber-300 border-amber-400/30",
  Contacted: "bg-blue-400/15 text-blue-200 border-blue-400/30",
  Quoted: "bg-purple-400/15 text-purple-200 border-purple-400/30",
  Scheduled: "bg-cyan-400/15 text-cyan-200 border-cyan-400/30",
  Completed: "bg-green-400/15 text-green-200 border-green-400/30",
  Closed: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0C]">
      <Seo title="Admin Dashboard" path="/admin" />
      <header className="sticky top-0 z-30 border-b border-[#1a1a22] bg-[#0A0A0C]/95 backdrop-blur-xl">
        <div className="container-kx flex h-16 items-center justify-between">
          <Logo size="sm" />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-neutral-400 sm:inline">{user?.email}</span>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 rounded-lg border border-[#2a2a34] px-4 py-2 text-sm font-semibold text-white hover:border-amber-400/60" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-kx py-8">
        <Tabs defaultValue="quotes">
          <TabsList className="mb-6 flex h-auto flex-wrap justify-start bg-[#101017]">
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quote Requests</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Our Work</TabsTrigger>
            <TabsTrigger value="site-media" data-testid="tab-site-media">Site Photos</TabsTrigger>
          </TabsList>
          <TabsContent value="quotes"><QuotesPanel /></TabsContent>
          <TabsContent value="reviews"><ReviewsPanel /></TabsContent>
          <TabsContent value="projects"><ProjectsPanel /></TabsContent>
          <TabsContent value="site-media"><SiteMediaPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function QuotesPanel() {
  const [data, setData] = useState({ quotes: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "All" ? { status: filter } : {};
      const { data } = await api.get("/admin/quotes", { params });
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load quotes"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateQuote = async (id, patch) => {
    try {
      const { data: updated } = await api.patch(`/admin/quotes/${id}`, patch);
      setSelected((s) => (s && s.id === id ? updated : s));
      load();
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  return (
    <div data-testid="quotes-panel">
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
        <StatCard label="Total" value={data.total} active={filter === "All"} onClick={() => setFilter("All")} />
        {STATUSES.map((s) => (
          <StatCard key={s} label={s} value={data.counts?.[s] || 0} active={filter === s} onClick={() => setFilter(s)} />
        ))}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-neutral-400">{filter === "All" ? "All requests" : `Status: ${filter}`}</p>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-amber-400" data-testid="quotes-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>
      ) : data.quotes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2a2a34] bg-[#101017] p-16 text-center">
          <Inbox className="mx-auto h-10 w-10 text-neutral-600" />
          <p className="mt-4 text-neutral-400">No quote requests {filter !== "All" ? `with status "${filter}"` : "yet"}.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#20202a]">
          <div className="divide-y divide-[#1a1a22]">
            {data.quotes.map((q) => (
              <button key={q.id} onClick={() => setSelected(q)} className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-white/[0.03]" data-testid={`quote-row-${q.id.slice(0, 6)}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <p className="truncate font-semibold text-white">{q.full_name}</p>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[0.65rem] font-bold uppercase ${STATUS_COLORS[q.status] || ""}`}>{q.status}</span>
                  </div>
                  <p className="truncate text-sm text-neutral-400">{q.service_needed} · {q.city}, {q.zip_code}</p>
                </div>
                <div className="hidden shrink-0 text-right text-xs text-neutral-500 sm:block">
                  {new Date(q.created_at).toLocaleDateString()}<br />
                  {new Date(q.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <QuoteDetailDialog quote={selected} onClose={() => setSelected(null)} onUpdate={updateQuote} />
    </div>
  );
}

function StatCard({ label, value, active, onClick }) {
  return (
    <button onClick={onClick} className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-amber-400 bg-amber-400/10" : "border-[#20202a] bg-[#101017] hover:border-[#33333f]"}`}>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
    </button>
  );
}

function QuoteDetailDialog({ quote, onClose, onUpdate }) {
  const [notes, setNotes] = useState("");
  useEffect(() => { setNotes(quote?.admin_notes || ""); }, [quote]);
  if (!quote) return null;

  const sched = [quote.preferred_date, quote.preferred_time].filter(Boolean).join(" · ");

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#26262f] bg-[#0f0f14] text-white sm:max-w-2xl" data-testid="quote-detail">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{quote.full_name}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <Select value={quote.status} onValueChange={(v) => onUpdate(quote.id, { status: v })}>
            <SelectTrigger className="w-44 bg-[#0f0f15] border-[#2a2a34]" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-xs text-neutral-500">Submitted {new Date(quote.created_at).toLocaleString()}</span>
        </div>

        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <a href={`tel:${quote.phone.replace(/\D/g, "")}`} className="flex items-center gap-2 rounded-lg border border-[#20202a] bg-[#101017] p-3 text-sm hover:border-amber-400/50"><Phone className="h-4 w-4 text-amber-400" /> {quote.phone}</a>
          <a href={`mailto:${quote.email}`} className="flex items-center gap-2 rounded-lg border border-[#20202a] bg-[#101017] p-3 text-sm hover:border-amber-400/50"><Mail className="h-4 w-4 text-amber-400" /> <span className="truncate">{quote.email}</span></a>
        </div>

        <div className="mt-1 space-y-2 rounded-lg border border-[#20202a] bg-[#101017] p-4 text-sm">
          <Row Icon={MapPin} label="Address">{quote.service_address}, {quote.city}, {quote.zip_code}</Row>
          <Row Icon={Inbox} label="Service">{quote.service_needed} · {quote.property_type}</Row>
          <Row Icon={Phone} label="Preferred contact">{quote.preferred_contact_method}</Row>
          {sched && <Row Icon={Calendar} label="Preferred schedule">{sched}</Row>}
        </div>

        <div>
          <p className="mb-1 text-xs uppercase tracking-widest text-neutral-500">Description</p>
          <p className="rounded-lg border border-[#20202a] bg-[#101017] p-4 text-sm leading-relaxed text-neutral-200">{quote.description}</p>
        </div>

        {quote.photo_ids?.length > 0 && (
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500"><ImageIcon className="h-3.5 w-3.5" /> Photos ({quote.photo_ids.length})</p>
            <div className="flex flex-wrap gap-2">
              {quote.photo_ids.map((id) => (
                <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer" className="h-24 w-24 overflow-hidden rounded-lg border border-[#2a2a34]">
                  <img src={fileUrl(id)} alt="Job photo" className="h-full w-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="mb-1 text-xs uppercase tracking-widest text-neutral-500">Internal notes</p>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="bg-[#0f0f15] border-[#2a2a34] text-white" data-testid="quote-notes" />
          <button onClick={() => onUpdate(quote.id, { admin_notes: notes })} className="mt-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-[#0A0A0C] hover:bg-amber-300" data-testid="quote-save-notes">Save notes</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
      <span className="text-neutral-500">{label}:</span>
      <span className="text-neutral-200">{children}</span>
    </div>
  );
}

function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setReviews(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load reviews"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moderate = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      load();
      toast.success(`Review ${status}`);
    } catch (err) {
      toast.error(formatApiError(err, "Action failed"));
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-400" /></div>;
  if (reviews.length === 0)
    return (
      <div className="rounded-2xl border border-dashed border-[#2a2a34] bg-[#101017] p-16 text-center">
        <Star className="mx-auto h-10 w-10 text-neutral-600" />
        <p className="mt-4 text-neutral-400">No reviews submitted yet.</p>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="reviews-panel">
      {reviews.map((r) => (
        <div key={r.id} className="rounded-xl border border-[#20202a] bg-[#101017] p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white">{r.name}</p>
                <span className={`rounded-full border px-2 py-0.5 text-[0.65rem] font-bold uppercase ${r.status === "approved" ? "border-green-400/30 bg-green-400/15 text-green-200" : r.status === "rejected" ? "border-red-400/30 bg-red-400/15 text-red-200" : "border-amber-400/30 bg-amber-400/15 text-amber-300"}`}>{r.status}</span>
              </div>
              <div className="mt-1 flex gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-neutral-600"}`} />)}</div>
              {r.location && <p className="mt-1 text-xs text-neutral-500">{r.location}</p>}
              <p className="mt-2 text-sm text-neutral-300">"{r.message}"</p>
            </div>
            <div className="flex shrink-0 gap-2">
              {r.status !== "approved" && (
                <button onClick={() => moderate(r.id, "approved")} className="flex h-9 w-9 items-center justify-center rounded-lg border border-green-400/40 text-green-300 hover:bg-green-400/10" title="Approve" data-testid={`review-approve-${r.id.slice(0,6)}`}><Check className="h-4 w-4" /></button>
              )}
              {r.status !== "rejected" && (
                <button onClick={() => moderate(r.id, "rejected")} className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-400/40 text-red-300 hover:bg-red-400/10" title="Reject" data-testid={`review-reject-${r.id.slice(0,6)}`}><X className="h-4 w-4" /></button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
